/**
 * Activity catalog.
 *
 * The catalog is the single source of truth for what a group can do together:
 * it maps a stable key to the AMap POI type code used for coverage checks and
 * to translation keys used by both the client and the email templates.
 */

export const ACTIVITY_KEYS = [
  'dining',
  'cafe',
  'ktv',
  'boardgame',
  'cinema',
  'shopping',
  'sports',
  'sightseeing',
] as const

export type ActivityKey = (typeof ACTIVITY_KEYS)[number]

export interface ActivityDefinition {
  key: ActivityKey
  /** AMap POI type code, see https://lbs.amap.com/api/webservice/guide/api/search */
  amapTypeCode: string
  /**
   * AMap type codes this activity owns, matched by longest declared prefix.
   *
   * AMap's taxonomy is a hierarchy whose parent nodes cover several catalog
   * activities (`0803` covers both KTV and board-game rooms), so a parent code
   * declared by two activities would let one answer for the other. Every entry
   * below is claimed by exactly one activity, classification takes the longest
   * matching prefix, and a code no activity declares stays `null` instead of
   * being guessed from a shared branch.
   */
  amapPrefixes: readonly string[]
  /** Translation key resolved through the shared i18n catalog. */
  labelKey: string
  /** Indoor activities are unaffected by weather; outdoor ones are. */
  indoor: boolean
  /** Typical duration in minutes, used when checking time windows. */
  typicalDurationMinutes: number
  /**
   * Chinese keyword used when a provider has no equivalent of AMap's type codes
   * and has to search by text.
   */
  searchKeyword: string
  /** Emoji used in compact UI chips. */
  icon: string
}

export const ACTIVITIES: Record<ActivityKey, ActivityDefinition> = {
  dining: {
    key: 'dining',
    amapTypeCode: '050000',
    /** AMap codes this activity owns; see `amapPrefixes` above. */
    amapPrefixes: ['050000', '050'],
    searchKeyword: '餐厅',
    labelKey: 'activity.dining',
    indoor: true,
    typicalDurationMinutes: 90,
    icon: '🍜',
  },
  cafe: {
    key: 'cafe',
    amapTypeCode: '050500',
    /** AMap codes this activity owns; see `amapPrefixes` above. */
    amapPrefixes: ['050500', '0505'],
    searchKeyword: '咖啡厅',
    labelKey: 'activity.cafe',
    indoor: true,
    typicalDurationMinutes: 60,
    icon: '☕️',
  },
  ktv: {
    key: 'ktv',
    amapTypeCode: '080302',
    /** AMap codes this activity owns; see `amapPrefixes` above. */
    amapPrefixes: ['080302'],
    searchKeyword: 'KTV',
    labelKey: 'activity.ktv',
    indoor: true,
    typicalDurationMinutes: 180,
    icon: '🎤',
  },
  boardgame: {
    key: 'boardgame',
    amapTypeCode: '080300',
    /** AMap codes this activity owns; see `amapPrefixes` above. */
    amapPrefixes: ['080300', '080304'],
    searchKeyword: '桌游吧',
    labelKey: 'activity.boardgame',
    indoor: true,
    typicalDurationMinutes: 180,
    icon: '🎲',
  },
  cinema: {
    key: 'cinema',
    amapTypeCode: '080601',
    /** AMap codes this activity owns; see `amapPrefixes` above. */
    amapPrefixes: ['080601', '0806'],
    searchKeyword: '电影院',
    labelKey: 'activity.cinema',
    indoor: true,
    typicalDurationMinutes: 150,
    icon: '🎬',
  },
  shopping: {
    key: 'shopping',
    amapTypeCode: '060100',
    /** AMap codes this activity owns; see `amapPrefixes` above. */
    amapPrefixes: ['060100', '060'],
    searchKeyword: '购物中心',
    labelKey: 'activity.shopping',
    indoor: true,
    typicalDurationMinutes: 120,
    icon: '🛍️',
  },
  sports: {
    key: 'sports',
    amapTypeCode: '080100',
    /** AMap codes this activity owns; see `amapPrefixes` above. */
    amapPrefixes: ['080100', '0801'],
    searchKeyword: '运动场馆',
    labelKey: 'activity.sports',
    indoor: false,
    typicalDurationMinutes: 120,
    icon: '🏸',
  },
  sightseeing: {
    key: 'sightseeing',
    amapTypeCode: '110000',
    /** AMap codes this activity owns; see `amapPrefixes` above. */
    amapPrefixes: ['110000', '110'],
    searchKeyword: '景点',
    labelKey: 'activity.sightseeing',
    indoor: false,
    typicalDurationMinutes: 150,
    icon: '🏞️',
  },
}

export function isActivityKey(value: string): value is ActivityKey {
  return (ACTIVITY_KEYS as readonly string[]).includes(value)
}

/**
 * Normalises an arbitrary list of activity keys into a de-duplicated,
 * catalog-ordered list. Unknown keys are dropped instead of failing the
 * request, so an older client cannot break an event.
 */
export function normalizeActivities(values: readonly string[]): ActivityKey[] {
  const unique = new Set(values.filter(isActivityKey))
  return ACTIVITY_KEYS.filter((key) => unique.has(key))
}

/**
 * Normalises the optional subset of an event's declared activities.
 *
 * An optional key that is not declared is dropped rather than rejected: the two
 * lists are edited together, and a request that removes an activity must not
 * also have to remember to remove its optional mark. The result is a subset of
 * `declared` in catalog order.
 */
export function normalizeOptionalActivities(
  values: readonly string[] | undefined,
  declared: readonly ActivityKey[],
): ActivityKey[] {
  if (!values || values.length === 0) {
    return []
  }
  const allowed = new Set(declared)
  return normalizeActivities(values).filter((key) => allowed.has(key))
}

/** Declared activities that are not optional: the round's hard requirement set. */
export function requiredActivities(
  declared: readonly ActivityKey[],
  optional: readonly ActivityKey[],
): ActivityKey[] {
  const skipped = new Set(optional)
  return declared.filter((key) => !skipped.has(key))
}
